import {Dialog} from '@headlessui/react';
import {CalendarIcon, SparklesIcon} from '@heroicons/react/24/outline';
import {Button} from '../../components/Button';
import {DateRangePicker} from '../../components/inputs/DateRangePicker';

export interface CreateTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onTitleChange: (title: string) => void;
  startDate: string | null;
  endDate: string | null;
  onDateChange: (startDate: string | null, endDate: string | null) => void;
  onCreateTrip: () => void;
  isLoading: boolean;
  isFormValid: boolean;
}

/**
 * Modal for creating a new trip.
 * Mobile-first design that collects trip title and date range before creation.
 */
export const CreateTripModal = ({
  isOpen,
  onClose,
  title,
  onTitleChange,
  startDate,
  endDate,
  onDateChange,
  onCreateTrip,
  isLoading,
  isFormValid,
}: CreateTripModalProps) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      {/* Full-screen container - mobile-first */}
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-surface shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-primary border-brand px-6 py-5 sm:px-8 sm:py-6">
              <Dialog.Title className="text-2xl sm:text-3xl  text-on-dark">
                ✈️ Create New Trip
              </Dialog.Title>
              <p className="text-sm text-on-dark/80 mt-1">Let's start with the basics</p>
            </div>

            {/* Form Content */}
            <div className="px-6 py-6 space-y-6 sm:px-8">
              {/* Trip Title Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm  text-main">
                  <SparklesIcon className="w-5 h-5 text-brand" />
                  Trip Title <span className="text-error">*</span>
                </label>
                <p className="text-xs text-muted">What should we call this adventure?</p>
                <div className="relative">
                  <input
                    id="trip-title"
                    name="trip-title"
                    type="text"
                    placeholder="e.g., Summer in Italy 🇮🇹"
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    className="block w-full px-4 py-3.5 sm:py-4 rounded-lg bg-surface border border-interactive
                      text-main text-base sm:text-lg placeholder:text-disabled
                      focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
                      transition-all shadow-sm hover:border-interactive-hover"
                    autoFocus
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm  text-main">
                  <CalendarIcon className="w-5 h-5 text-brand" />
                  Trip Dates <span className="text-error">*</span>
                </label>
                <p className="text-xs text-muted mb-3">When are you traveling?</p>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={onDateChange}
                  startDateId="create-trip-start-date"
                  endDateId="create-trip-end-date"
                  disabled={isLoading}
                />
              </div>

              {/* Helper Text */}
              <div className="bg-muted rounded-lg p-4 border border-subtle">
                <p className="text-xs text-muted leading-relaxed">
                  💡 <span className="">Tip:</span> You can add photos, descriptions, and
                  customize everything after creating your trip.
                </p>
              </div>
            </div>

            {/* Action Buttons - Mobile-first stacking */}
            <div className="bg-muted border-t border-subtle px-6 py-5 sm:px-8">
              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={onClose}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={onCreateTrip}
                  disabled={!isFormValid || isLoading}
                  loading={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? 'Creating Trip...' : 'Create Trip'}
                </Button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};
